import { Checkbox } from 'antd'
import Autolinker, { Match } from 'autolinker'
import { isNil, keyBy } from 'lodash'
import React from 'react'
import ReactMarkdown from 'react-markdown'
// @ts-ignore
import { listItem } from 'react-markdown/lib/renderers'
import { Link } from 'react-router-dom'
import gfm from 'remark-gfm'
// @ts-ignore
import linkifyRegex from 'remark-linkify-regex'

import { UserHoverCard } from '@/components'
import { RequestedPostMentionFragment } from '@/generated/graphql'
import { routes } from '@/routes'

import { Highlighter, highlighterStyle } from './highlight'
import s from './text-content.module.less'

export interface TextContentProps {
  content: string
  isMarkdown?: boolean
  mentions: RequestedPostMentionFragment[]
  onChange?: (content: string) => void
}

const intoPairs = (xs: number[]) => xs.slice(1).map((x, i) => [xs[i], x])
const breakAt = (places: number[], str: string) =>
  intoPairs([0, ...places, str.length]).map(([a, b]) => str.substring(a, b))
const breakWhere = (words: Match[], str: string) =>
  breakAt(
    words.reduce((a, m) => [...a, m.getOffset(), m.getOffset() + m.getMatchedText().length], [] as number[]),
    str
  )

export const SiteLink: React.FC<{ href: string; mentions?: RequestedPostMentionFragment[] }> = ({
  href,
  mentions = [],
  children,
}) => {
  if (!href) {
    return <span>{children}</span>
  }

  if (href.startsWith(window.location.origin)) {
    return <Link to={href.replace(window.location.origin, '')}>{children}</Link>
  }

  if (href.startsWith('@')) {
    const mentionUserName = href.substring(1)
    const usersMap = keyBy(mentions, (m) => m.user.email.split('@')[0])
    if (usersMap[mentionUserName]) {
      return (
        <UserHoverCard email={usersMap[mentionUserName].user.email}>
          <Link onClick={(e) => e.stopPropagation()} to={routes.users.page(mentionUserName)}>
            @{usersMap[mentionUserName]?.user.name || mentionUserName}
          </Link>
        </UserHoverCard>
      )
    } else {
      return <span>{children}</span>
    }
  }

  if (href.startsWith('itms-apps')) {
    return <a href={href}>{children}</a>
  }

  if (!href.startsWith('http')) {
    href = 'http://' + href
  }

  return (
    <a href={href} target='_blank' rel='noreferrer'>
      {children}
    </a>
  )
}

export const NormalTextContent: React.FC<Omit<TextContentProps, 'onChange'>> = ({
  content,
  mentions: mentionsData,
  ...props
}) => {
  const mentions = Autolinker.parse(content, {
    urls: true,
    email: false,
    phone: false,
    mention: 'instagram',
    stripPrefix: false,
  })

  const chunks = breakWhere(mentions, content).map((s, i) => {
    let node: React.ReactNode = s

    if (i % 2 !== 0) {
      const m = mentions[(i - 1) / 2]
      switch (m.getType()) {
        case 'url':
        case 'mention':
          node = (
            <SiteLink href={s} mentions={mentionsData}>
              {s}
            </SiteLink>
          )
      }
    }

    return <React.Fragment key={i}>{node}</React.Fragment>
  })

  return <div {...props}>{chunks}</div>
}

const transformLinkUri = (uri: string) => {
  const protocols = ['http', 'https', 'itms-apps']
  const url = (uri || '').trim()
  const first = url.charAt(0)

  if (first === '#' || first === '/') {
    return url
  }

  const colon = url.indexOf(':')
  if (colon === -1) {
    return url
  }

  const length = protocols.length
  let index = -1

  while (++index < length) {
    const protocol = protocols[index]

    if (colon === protocol.length && url.slice(0, protocol.length).toLowerCase() === protocol) {
      return url
    }
  }

  index = url.indexOf('?')
  if (index !== -1 && colon > index) {
    return url
  }

  index = url.indexOf('#')
  if (index !== -1 && colon > index) {
    return url
  }

  return ''
}

const generateCheckbox = (checked: boolean) => (checked ? '- [x]' : '- [ ]')
const noop = () => {}

export const TextContent: React.FC<TextContentProps> = React.memo(
  ({ content, mentions, isMarkdown = false, onChange = noop }) => {
    if (!isMarkdown) {
      return <NormalTextContent mentions={mentions} content={content} className={s.normalText} />
    }

    return (
      <ReactMarkdown
        className='markdown-body'
        source={content}
        plugins={[gfm, linkifyRegex(/@[A-Za-z0-9.]+\b/), linkifyRegex(/itms-apps:\/\/[^\s]+\b/)]}
        rawSourcePos={true}
        transformLinkUri={transformLinkUri}
        renderers={{
          code: ({ language = '', value = '' }) => {
            // eslint-disable-next-line react/no-children-prop
            return <Highlighter style={highlighterStyle} language={language} children={value} />
          },
          link: ({ children, ...props }) => {
            return (
              <SiteLink mentions={mentions} {...props}>
                {children}
              </SiteLink>
            )
          },
          listItem: (props) => {
            if (isNil(props.checked)) {
              return listItem(props)
            }
            const { children, checked } = props
            const onCheckboxChange = () => {
              const lines = content.split('\n')
              const lineIndex = props.sourcePosition.start.line - 1
              lines[lineIndex] = lines[lineIndex].replace(generateCheckbox(checked), generateCheckbox(!checked))
              onChange(lines.join('\n'))
            }
            return (
              <div style={{ marginLeft: '-2em' }}>
                <Checkbox checked={checked} onChange={onCheckboxChange}>
                  {children}
                </Checkbox>
              </div>
            )
          },
        }}
      />
    )
  }
)
